import { Navbar } from '@/components/Navbar'
import { Footer } from '@/components/Footer'
import React, { memo, Suspense, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { FiUploadCloud, FiDownload } from 'react-icons/fi'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'
import { IoIosInformationCircleOutline } from 'react-icons/io'

interface Props {}

export const Analyze: React.FC<Props> = memo(() => {
  const [flow, setFlow] = useState<
    'upload' | 'uploaded' | 'processing' | 'processed'
  >('upload')
  const [pixelSize, setPixelSize] = useState<number>()
  const [focalLength, setFocalLength] = useState<number>()
  const [magnitudeFilter, setMagnitudeFilter] = useState<number>(5)
  const [tetraVal, setTetraVal] = useState<number>(0)
  const [modalVisible, setModalVisible] = useState<boolean>(false)

  const [output, setOutput] = useState({
    cmdOutput: '',
    attitudeString: '',
    base64Image: '',
    processingTime: 0
  })

  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: 1,
    onDrop: async (files) => {
      const file = files[0]
      setSelectedFile(file)
      const base64 = (await toBase64(file)) as string
      setUploadedImage(base64)
      setFlow('uploaded')
    },
  })

  const [submitPressed, setSubmitPressed] = useState(false)

  const [uploadedImage, setUploadedImage] = useState<string | null>(null)

  const loadDemoImage = async (src: string, defaultPixelSize: number, defaultFocalLength: number, defaultTetra: number = 0) => {
    const res = await fetch(src)
    const blob = await res.blob()
    const file = new File([blob], src.split('/').pop()!, { type: blob.type })
    setSelectedFile(file)
    setPixelSize(defaultPixelSize)
    setFocalLength(defaultFocalLength)
    setTetraVal(defaultTetra)
    setMagnitudeFilter(5)
    const base64 = (await toBase64(file)) as string
    setUploadedImage(base64)
    setFlow('uploaded')
  }

  const clearImage = () => {
    setSelectedFile(null)
    setUploadedImage(null)
    setFlow('upload')
  }

  const submitButtonPressed = async (): Promise<void> => {
    if (!selectedFile) return

    setFlow('processing')

    const res = await fetch(
      'https://lost-backend-1036293057238.us-central1.run.app/analyze',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: await toBase64(selectedFile),
          focalLength,
          pixelSize,
          centroidMagnitudeFilter: magnitudeFilter,
          tetra: tetraVal,
        }),
      },
    )

    const data = await res.text()
    if (res.status !== 200) {
      setFlow('upload')
      return alert(data)
    }

    const parsedData = JSON.parse(data)
    const dataLabels = [
      'Known',
      'Right Ascension',
      'Declination',
      'Roll',
      'i',
      'j',
      'k',
      'Real',
    ]

    setOutput({
      cmdOutput: parsedData.command_line_data,
      attitudeString: Object.entries(parsedData.image_analysis)
        .map(
          ([key, value], index) =>
            `<strong>${dataLabels[index]}:</strong> ${
              value === 1 ? 'True' : value === 0 ? 'False' : value
            }`,
        )
        .join('<br>'),
      base64Image: 'data:image/jpeg;base64,' + parsedData.annotated_image,
      processingTime: parsedData.processingTime,
    })
    setFlow('processed')
  }

  useEffect(() => {
    if (submitPressed && selectedFile) {
      async function process() {
        setFlow('processing')

        //https://lost-backend-1036293057238.us-central1.run.app/analyze
        //http://localhost:3000/analyze

        const res = await fetch(
          'https://lost-backend-1036293057238.us-central1.run.app/analyze',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              image: await toBase64(selectedFile),
              focalLength,
              pixelSize,
              centroidMagnitudeFilter: magnitudeFilter,
              tetra: tetraVal,
            }),
          },
        )
        const data = await res.text()
        if (res.status !== 200) {
          setFlow('upload')
          return alert(data)
        } else {
          const parsedData = JSON.parse(data)
          const dataLabels = [
            'Known',
            'Right Ascension',
            'Declination',
            'Roll',
            'i',
            'j',
            'k',
            'Real',
          ]
          let commandLineData = ''
          for (let i = 0; i < parsedData.length; i++) {
            parsedData.command_line_data
          }
          setOutput({
            cmdOutput: parsedData.command_line_data,
            attitudeString: Object.entries(parsedData.image_analysis)
              .map(
                ([key, value], index) =>
                  `<strong>${dataLabels[index]}:</strong> ${value == 1 ? 'True' : value == 0 ? 'False' : value}`,
              )
              .join('<br>'), // Use <br> instead of \n for proper HTML rendering
            base64Image: 'data:image/jpeg;base64,' + parsedData.annotated_image,
            processingTime: parsedData.processingTime,
          })
          // setOutput({
          //     cmdOutput: parsedData.command_line_data,
          //     attitudeString: parsedData.attitude_string,
          //     base64Image: parsedData.annotated_image
          // })
          setFlow('processed')
        }
      }
      process()
      setSubmitPressed(false)
    }
  }, [pixelSize, focalLength, magnitudeFilter, selectedFile])

  useEffect(() => {
    if (!modalVisible) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalVisible(false)
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [modalVisible])

  return (
    <>
      <Navbar activePath="/analyze" />
      <div className="w-full flex justify-center gap-4 mt-6">
        <div className="flex">
          <button
            onClick={() => loadDemoImage('/lost_synthetic.png', 22.2, 35)}
            className="rounded-l-lg border border-gray-400 text-gray-700 text-[14px] py-2 px-4 cursor-pointer hover:bg-gray-100"
          >
            <span>LOST-generated Synthetic Image</span>
          </button>
          <a
            href="/lost_synthetic.png"
            download="lost_synthetic.png"
            className="flex items-center border border-l-0 border-gray-400 rounded-r-lg px-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <FiDownload className="text-sm" />
          </a>
        </div>
        <div className="flex">
          <button
            onClick={() => loadDemoImage('/observatory.png', 22.2, 35)}
            className="rounded-l-lg border border-gray-400 text-gray-700 text-[14px] py-2 px-4 cursor-pointer hover:bg-gray-100"
          >
            <span>Observatory Image</span>
          </button>
          <a
            href="/observatory.png"
            download="observatory.png"
            className="flex items-center border border-l-0 border-gray-400 rounded-r-lg px-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <FiDownload className="text-sm" />
          </a>
        </div>
        <div className="flex">
          <button
            onClick={() => loadDemoImage('/mount_st_helens.png', 4.1, 4.2, 1)}
            className="rounded-l-lg border border-gray-400 text-gray-700 text-[14px] py-2 px-4 cursor-pointer hover:bg-gray-100"
          >
            <span>Mount St. Helens Image</span>
          </button>
          <a
            href="/mount_st_helens.png"
            download="mount_st_helens.png"
            className="flex items-center border border-l-0 border-gray-400 rounded-r-lg px-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <FiDownload className="text-sm" />
          </a>
        </div>
      </div>
      <div className="w-full flex items-stretch justify-center mt-12 gap-5">
        <div className="w-[500px] ">
          {flow === 'upload' && (
            <section className="w-full border-dashed border-2 px-8 py-6 border-gray-400 rounded-2xl flex items-center justify-center">
              <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <div className="w-full justify-center items-center flex flex-col">
                  <FiUploadCloud className="text-xl mt-3" />
                  <h3 className="mt-6 font-bold text-gray-800 text-[14px]">
                    Choose a file or drag & drop it here
                  </h3>
                  <h3 className="mt-2 text-gray-500 text-[13px]">
                    JPG and PNG images, up to 5MB
                  </h3>
                  <button className="rounded-lg border border-gray-400 text-gray-700 text-[13px] py-1 px-3 mt-4 cursor-pointer">
                    Browse File
                  </button>
                </div>
              </div>
            </section>
          )}
          {flow === 'processing' && (
            <section className="w-full border-dashed border-2 px-8 py-2 border-gray-400 rounded-2xl flex items-center justify-center">
              <div>
                <input />
                <div className="w-full justify-center items-center flex flex-col">
                  <h3 className="font-bold text-gray-800 text-[14px]">
                    Processing
                  </h3>
                  <AiOutlineLoading3Quarters className="text-xl mt-3 mb-6 animate-spin" />
                </div>
              </div>
            </section>
          )}
          {flow === 'uploaded' && uploadedImage && (
            <section className="w-full border-dashed border-2 px-8 py-2 border-gray-400 rounded-2xl flex items-center justify-center">
              <div className="w-full justify-center items-center flex flex-col">
                <h3 className="font-bold text-gray-800 text-[20px]">
                  Original Image:
                </h3>
                <img
                  onClick={() => setModalVisible(true)}
                  className="rounded-lg mt-4 mb-4 cursor-pointer"
                  src={uploadedImage}
                  alt="Uploaded Image"
                />
                <button
                  onClick={clearImage}
                  className="rounded-lg border border-gray-400 text-gray-700 text-[13px] py-1 px-3 mb-3 cursor-pointer hover:bg-gray-100"
                >
                  Remove
                </button>
              </div>
            </section>
          )}
          {flow === 'processed' && (
            <>
              <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <button className="w-full cursor-pointer rounded-lg border-2 border-gray-400 py-2 px-4 mb-3 font-bold text-gray-800 text-[16px] hover:bg-gray-100 flex items-center justify-center gap-2">
                  <FiUploadCloud className="text-lg" />
                  Upload New Image
                </button>
              </div>
              <section className="w-full border-dashed border-2 px-8 py-2 border-gray-400 rounded-2xl flex items-center justify-center">
                <div>
                  <input />
                  <div className="w-full justify-center items-center flex flex-col">
                    <h3 className="font-bold text-gray-800 text-[20px]">
                      Annotated Image:
                    </h3>
                    <img
                      onClick={() => {
                        setModalVisible(true)
                      }}
                      className="rounded-lg mt-4 mb-4 cursor-pointer"
                      src={output.base64Image}
                      alt="Annotated Image"
                    />
                  </div>
                </div>
              </section>
              <div className="mt-4">
                <h2 className="font-bold mb-2">Attitude Data</h2>
                <pre
                  className="text-[12px] bg-gray-200 px-3 py-2"
                  dangerouslySetInnerHTML={{ __html: output.attitudeString }}
                />
              </div>
              <div className="mt-4">
                <h2 className="font-bold mb-2">Log Output</h2>
                <pre className="text-[12px] bg-gray-200 px-3 py-2">
                  {output.cmdOutput}
                </pre>
              </div>
              <div className="mt-4 mb-8">
                <h2 className="font-bold mb-2">Processing Time - Only includes LOST processing</h2>
                <pre className="text-[12px] bg-gray-200 px-3 py-2">
                  {Number(output.processingTime).toFixed(2)}ms
                </pre>
              </div>
            </>
          )}
        </div>
        <div className="w-[400px] bg-gray-50 px-4 rounded-2xl py-2">
          <div className="flex gap-1 flex-col">
            <span className="text-sm font-bold">
              Pixel Size (side length, in µm, of each pixel)
            </span>
            <input
              type="number"
              className="border border-gray-400 rounded-md px-2 py-1 text-sm"
              min={1}
              max={100}
              placeholder="Pixel Size (µm)"
              value={pixelSize}
              onChange={(e) => setPixelSize(Number(e.target.value))}
            />
          </div>
          <div className="flex gap-1 flex-col mt-2">
            <span className="text-sm font-bold">Focal Length (in mm)</span>
            <input
              type="number"
              className="border border-gray-400 rounded-md px-2 py-1 text-sm"
              min={1}
              max={600}
              placeholder="Focal Length (mm)"
              value={focalLength}
              onChange={(e) => setFocalLength(Number(e.target.value))}
            />
          </div>
          <div className="flex gap-1 flex-col mt-2">
            <span className="text-sm font-bold flex items-center gap-1">
              Centroid Magnitude Filter{' '}
              <div>
                <IoIosInformationCircleOutline className="mt-[1.5px] peer" />
                <div className="absolute font-normal px-2 py-1 bg-white rounded-md border border-gray-400 max-w-[30%] translate-x-1 translate-y-1 hidden peer-hover:block">
                  The centroid magnitude filter may also need to be adjusted,
                  depending on the resolution and noisiness of your images. If
                  the output file has many centrorids (red boxes) where there
                  are no visible stars, then the filter should be increased. If
                  there are many stars without centroids, the filter should be
                  decreased.
                </div>
              </div>
            </span>
            <input
              type="number"
              className="border border-gray-400 rounded-md px-2 py-1 text-sm"
              min={1}
              max={10}
              placeholder="Centroid Magnitude Filter"
              value={magnitudeFilter}
              onChange={(e) => setMagnitudeFilter(Number(e.target.value))}
            />
          </div>
          <div className="flex gap-1 flex-col mt-2">
            <span className="text-sm font-bold flex items-center gap-1">
              Tetra{' '}
              <div>
                <IoIosInformationCircleOutline className="mt-[1.5px] peer" />
                <div className="absolute font-normal px-2 py-1 bg-white rounded-md border border-gray-400 max-w-[30%] translate-x-1 translate-y-1 hidden peer-hover:block">
                  An alternative algorithm for star tracking
                </div>
              </div>
            </span>
            <input
              type="checkbox"
              className="self-start w-5 h-5"
              checked={tetraVal === 1}
              onChange={() => setTetraVal(tetraVal === 1 ? 0 : 1)}
            />
          </div>
          <div className="flex justify-center p-4">
            <button
              type="submit"
              className="cursor-pointer border-solid py-1 px-2 border-2 rounded-lg hover:bg-black hover:text-white"
              onClick={submitButtonPressed}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
      {modalVisible && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
          onClick={() => setModalVisible(false)}
        >
          <div
            className="w-[90%] h-[90%] bg-gray-100 rounded-3xl overflow-hidden p-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="absolute top-4 right-4 cursor-pointer font-bold text-lg z-10"
              onClick={() => {
                setModalVisible(false)
              }}
            >
              X
            </div>
            <div
              className="w-full h-full bg-contain bg-no-repeat bg-center"
              style={{
                backgroundImage: `url(${flow === 'processed' ? output.base64Image : uploadedImage})`,
              }}
            ></div>
          </div>
        </div>
      )}
    </>
  )
})
Analyze.displayName = 'Index'

const toBase64 = (file: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
  })
